// Re:Vault extensions - the module registry. Re:Vault is assembled from small
// opt-in modules: network-level ones run in the service worker, page-tool ones
// inject into proxied tabs, and workspace ones are app features that were once
// "base" and are now toggleable modules. Every entry is just a settings flag
// (`key` MUST match a DEFAULTS entry in settings-schema.js) - a disabled
// extension costs nothing at runtime. Former base features reuse their original
// settings key, so flipping them here is the same as flipping the old toggle
// (no migration, and their existing settings sections stay in sync).
//
// `sw` names the config field the service worker receives for network-blocking
// extensions (see app/extensions.js + sw/sw.js). Everything else is app-side.
//
// `toolbar` says how it gets a toolbar button (shared/ext-pins.js): "auto" gets one as soon as it is
// turned on, "pin" only when pinned by hand, and none never (keyboard and address bar features).
// `quiet` ones are browsing features and don't count on the extensions badge.

const I = (body) => '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + body + "</svg>";

export const EXT_CATEGORIES = ["Page tools", "Faster pages", "For developers", "Study", "Browsing"];

// Is this extension available here? One behind a server switch (`flag`) is hidden while it's off.
export function extAvailable(x) {
	if (!x.flag) return true;
	try { return (localStorage.getItem(x.flag) || "off") !== "off"; } catch { return false; }
}

export const EXTENSIONS = [
	// --- Faster pages (network - enforced by the service worker) ---
	{
		id: "adblock",
		key: "extAdblock",
		sw: "adblock",
		label: "Ad Blocker",
		category: "Faster pages",
		toolbar: "pin",
		icon: I('<circle cx="12" cy="12" r="9"></circle><line x1="5.5" y1="5.5" x2="18.5" y2="18.5"></line>'),
		desc: "Blocks ads and trackers on the sites you open. Re:Vault's own pages are never affected.",
	},
	{
		id: "bandwidth",
		key: "extBandwidth",
		sw: "bandwidth",
		label: "Bandwidth Saver",
		category: "Faster pages",
		toolbar: "pin",
		icon: I('<path d="M4 14 a8 8 0 0 1 16 0"></path><line x1="12" y1="14" x2="16" y2="8"></line><circle cx="12" cy="14" r="1.4"></circle>'),
		desc: "Blocks autoplay video and heavy media. Helps on slow Wi-Fi and older laptops.",
	},

	// --- Page tools (injected into the sites you open; DevTools is one too) ---
	{
		id: "devtools",
		key: "extDevtools",
		label: "DevTools",
		category: "For developers",
		toolbar: "auto",
		icon: I('<polyline points="8,9 5,12 8,15"></polyline><polyline points="16,9 19,12 16,15"></polyline><line x1="13" y1="7" x2="11" y2="17"></line>'),
		desc: "Console, elements, network and storage on the sites you open.",
	},
	{
		id: "darkmode",
		key: "extDarkMode",
		label: "Dark Web Pages",
		category: "Page tools",
		toolbar: "auto",
		icon: I('<path d="M20 13 A8 8 0 1 1 11 4 A6.5 6.5 0 0 0 20 13 Z"></path>'),
		desc: "Makes bright sites dark. Pause it from its button if a page looks odd.",
	},
	{
		id: "readaloud",
		key: "extReadAloud",
		label: "Read Aloud",
		category: "Page tools",
		toolbar: "auto",
		icon: I('<path d="M4 10 v4 h3 l4 4 V6 L7 10 Z"></path><path d="M15 9 a4 4 0 0 1 0 6"></path><path d="M17.5 6.5 a8 8 0 0 1 0 11"></path>'),
		desc: "Reads the page out loud, one sentence at a time, and highlights where it is.",
	},
	{
		id: "highlighter",
		key: "extHighlighter",
		label: "Highlighter",
		category: "Page tools",
		toolbar: "auto",
		icon: I('<path d="M8 20 H4 v-3 l9-9 3 3 -9 9 Z"></path><path d="M12 8 l4-4 a1.4 1.4 0 0 1 2 0 l2 2 a1.4 1.4 0 0 1 0 2 l-4 4"></path>'),
		desc: "Select text on the sites you open to highlight it.",
	},
	{
		id: "pip",
		key: "extPip",
		label: "Picture-in-Picture",
		category: "Page tools",
		toolbar: "auto",
		icon: I('<rect x="3" y="5" width="18" height="14" rx="2"></rect><rect x="12" y="11" width="7" height="5" rx="1"></rect>'),
		desc: "Pops a page's video into a small window that stays on top.",
	},

	// --- Study and browsing features (they keep their original settings keys) ---
	{
		id: "bell",
		key: "bellEnabled",
		label: "Class Bell",
		category: "Study",
		toolbar: "pin",
		icon: I('<path d="M12 4 a6 6 0 0 1 6 6 v3 l1.5 2.5 H4.5 L6 13 v-3 a6 6 0 0 1 6-6 Z"></path><path d="M10 19 a2 2 0 0 0 4 0"></path>'),
		desc: "A heads-up before your school's bell rings. Set your bell times in Settings, Study and focus.",
	},
	{
		id: "music",
		key: "musicEnabled",
		label: "Background Music",
		category: "Study",
		toolbar: "pin",
		icon: I('<circle cx="7" cy="17" r="2.5"></circle><circle cx="17" cy="15" r="2.5"></circle><path d="M9.5 17 V7 l10-2 v10"></path>'),
		desc: "Focus sounds and your own music in a small player.",
	},
	{
		id: "palette",
		key: "paletteEnabled",
		label: "Command Palette",
		category: "Browsing",
		quiet: true,
		icon: I('<rect x="3" y="5" width="18" height="14" rx="2"></rect><polyline points="7,9 10,12 7,15"></polyline><line x1="12" y1="15" x2="16" y2="15"></line>'),
		desc: "Ctrl+K opens a quick finder for pages, tools and settings.",
	},
	{
		id: "helper",
		key: "extHelper",
		label: "Study Helper",
		category: "Study",
		// listed only while the server has the Helper on for this browser (app/helper-dock.js keeps it)
		flag: "revault:helper:flag",
		// it has its own toolbar button (app/helper-dock.js), so no extension button and no badge count
		quiet: true,
		icon: I('<path d="M12 3l1.8 3.9 4.2.6-3 2.9.7 4.2L12 12.6 8.3 14.6l.7-4.2-3-2.9 4.2-.6z"></path><path d="M6 20h12"></path>'),
		desc: "An AI study tool that explains, quizzes you, checks your work and makes flashcards. Turning it off hides its button and side panel.",
	},
	{
		id: "suggestions",
		key: "suggestions",
		label: "Search Suggestions",
		category: "Browsing",
		quiet: true,
		icon: I('<circle cx="10" cy="10" r="6"></circle><line x1="14.5" y1="14.5" x2="20" y2="20"></line>'),
		desc: "Live suggestions as you type in the address bar.",
	},
];

export const EXTENSION_MAP = Object.fromEntries(EXTENSIONS.map((x) => [x.id, x]));

// Entries the service worker enforces (network blocking). app/extensions.js
// relays `{ [sw]: boolean }` built from these.
export const SW_EXTENSIONS = EXTENSIONS.filter((x) => x.sw);

// Settings > Extensions and the toolbar badge count only these.
export const PAGE_EXTENSIONS = EXTENSIONS.filter((x) => !x.quiet && x.category !== "Study");
export const EXTENSION_BY_KEY = Object.fromEntries(EXTENSIONS.map((x) => [x.key, x]));
